import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
     
variant: {
  default:
    "bg-primary text-white-both-modes focus:text-white-both-modes shadow-xs focus-visible:ring-3 focus-visible:ring-secondary hover:bg-primary-600 disabled:bg-bg-200 disabled:text-text-100",
  destructive:
  "bg-error-500 text-white-both-modes shadow-xs hover:bg-error-700 focus-visible:ring-3  disabled:bg-bg-200 disabled:text-text-100 focus-visible:ring-secondary",
  outline:
    "border border-border text-foreground dark:text-foreground shadow-xs focus-visible:ring-2 focus-visible:ring-[hsl(var(--border))] hover:bg-card/40 dark:hover:bg-card/60 disabled:text-text-100 disabled:border-bg-200",
  secondary:
    "bg-secondary text-white-both-modes shadow-xs hover:bg-secondary/80 focus-visible:ring-3 focus-visible:ring-secondary",
  ghost:
    "disabled:text-text-100 focus-visible:ring-3 hover:dark:text-foreground hover:dark:bg-card focus-visible:ring-secondary text-text-200 hover:bg-secondary-100 hover:text-primary",
  link: "text-primary underline-offset-4 focus-visible:ring-3  focus-visible:ring-secondary hover:underline disabled:text-text-100",
},
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-4 text-xs",
        lg: "h-10 rounded-md px-4",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, startIcon, endIcon, loading, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {loading ? (
          <span className="mr-2">Loading...</span>
        ) : (
          <>
            {startIcon && <span className="mr-1`">{startIcon}</span>}
            {children}
            {endIcon && <span className="ml-1">{endIcon}</span>}
          </>
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button"

export { Button, buttonVariants }
